<template>
  <FlotoContentLoader :loading="loading">
    <MRow v-if="!disabled">
      <MCol class="flex justify-end mb-2">
        <AssetDepreciationDrawer
          :module-name="moduleName"
          :resource="resource"
          :resource-id="resourceId"
          :default-data="assetDepreciationInfo"
          @refresh="refresh"
        />
      </MCol>
    </MRow>
    <AssetDepreciationDetails
      :asset-depreciation-info="assetDepreciationInfo"
      :cost-info="costInfo"
    />
    <div class="flex flex-col flex-1">
      <MRow>
        <MCol class="flex justify-end mt-2 mb-2">
          <MDropdown
            :options="filterOptions"
            :value="(filterType || {}).value"
            @change="handleFilterChange($event)"
          >
            <template v-slot:trigger>
              <MButton variant="transparent" :shadow="false">
                {{
                  filterType
                    ? (filterOptions.find((o) => o.key === filterType) || {})
                        .text
                    : ''
                }}
                <MIcon class="mx-1" name="filter" size="lg" />
              </MButton>
            </template>
          </MDropdown>
          <ExportButton
            v-if="showDownloadButton"
            :button-text="$t('download')"
            class="mx-1"
            :header="`${$t('download')}`"
            :file-name="$tc(exportModuleName, 2)"
            allow-pdf
            :options="[
              { text: $t('pdf'), value: 'pdf' },
              { text: $t('excel'), value: 'excel' },
              { text: $t('csv'), value: 'csv' },
            ]"
            :default-data="{ exportFormat: 'pdf' }"
            :download-fn="handleDownloadAssetDepreciationDetails"
          />
        </MCol>
      </MRow>
      <PaginatedCrud
        :key="renderCount"
        ref="list"
        as-table
        prepend-new-item
        :scrollable="false"
        :fetch-fn="fetchFnTableList"
        :columns="tableColumns"
        :show-no-data="false"
      >
        <template v-slot:add-controls>
          <span />
        </template>
        <template v-slot:form>
          <span />
        </template>
        <template v-slot:amount="{ item }">
          <td class="text-ellipsis">
            <div v-if="item.amount" class="flex">
              {{ `${item.amount}` }}
              <CurrencyPicker :value="item.currencyId" text-only disabled />
            </div>
            <div v-else> --- </div>
          </td>
        </template>
        <template v-slot:accumulatedAmount="{ item }">
          <td class="text-ellipsis">
            <div v-if="item.accumulatedAmount" class="flex">
              {{ `${item.accumulatedAmount}` }}
              <CurrencyPicker :value="item.currencyId" text-only disabled />
            </div>
            <div v-else> --- </div>
          </td>
        </template>
        <template v-slot:bookValue="{ item }">
          <td class="text-ellipsis">
            <div v-if="item.bookValue" class="flex">
              {{ `${item.bookValue}` }}
              <CurrencyPicker :value="item.currencyId" text-only disabled />
            </div>
            <div v-else> --- </div>
          </td>
        </template>
      </PaginatedCrud>
    </div>
  </FlotoContentLoader>
</template>

<script>
import PaginatedCrud from '@components/paginated-crud'
import { OrganizationComputed } from '@state/modules/organization'
import {
  getAssetDepreciationInfoApi,
  assetDepreciationTableApi,
  downloadDepreciationApi,
} from '../../asset-api'
import AssetDepreciationDetails from './asset-depreciation-details'
import AssetDepreciationDrawer from './asset-depreciation-drawer.vue'
import CurrencyPicker from '@components/data-picker/currency-picker'
import ExportButton from '@modules/reports/components/export-button'
import { getEncryptedPassword } from '@utils/password'
import { transformSearchCriteriasForServer } from '@data/search'
export default {
  name: 'DepreciationDetailsTab',
  components: {
    AssetDepreciationDetails,
    AssetDepreciationDrawer,
    PaginatedCrud,
    CurrencyPicker,
    ExportButton,
  },
  props: {
    resourceId: { type: Number, required: true },
    resource: { type: Object, required: true },
    moduleName: { type: String, required: true },
    costInfo: {
      type: Object,
      default() {
        return {}
      },
    },
    disabled: { type: Boolean, default: false },
  },
  data() {
    this.exportModuleName = 'asset_depreciation'
    this.filterOptions = [
      { text: `${this.$t('monthly')}`, key: 'month' },
      {
        text: `${this.$t('annually')}`,
        key: 'year',
      },
    ]
    return {
      loading: false,
      assetDepreciationInfo: {},
      filterType: 'month',
      renderCount: 1,
      showDownloadButton: false,
    }
  },
  computed: {
    ...OrganizationComputed,
    tableColumns() {
      return [
        {
          name:
            this.filterType === 'month'
              ? `${this.$tc('months')}/${this.$tc('years')}`
              : `${this.$tc('years')}`,
          key: 'depreciationDate',
        },
        { name: `${this.$tc('depreciation')}`, key: 'amount' },
        {
          name: `${this.$tc('accumulated')} ${this.$tc('depreciation')}`,
          key: 'accumulatedAmount',
        },
        { name: `${this.$tc('book')} ${this.$tc('value')}`, key: 'bookValue' },
        {
          name: `${this.$tc('remaining')} ${this.$tc('life')}`,
          key: 'remainingLife',
        },
      ]
    },
    defaultSelectedColumns() {
      return this.tableColumns.map((c) => c.key)
    },
  },
  created() {
    this.getAssetDepreciationInfo()
  },
  methods: {
    getAssetDepreciationInfo() {
      getAssetDepreciationInfoApi(this.moduleName, this.resourceId, {
        archived: this.resource.archived,
      })
        .then((data) => {
          this.assetDepreciationInfo = {
            // set default data for configuration drawer
            depreciationDerivation: 'product_level',
            depreciationMethod: null,
            depreciationType: 'useful_life',
            ...data,
            id: data.id,
            acquitionTime: this.resource.acquitionTime,
          }
          this.loading = false
        })
        .catch((e) => {
          this.assetDepreciationInfo = {
            depreciationDerivation: 'product_level',
            acquitionTime: this.resource.acquitionTime,
          }
          this.loading = false
        })
    },
    handleFilterChange(option) {
      this.filterType = option.key

      this.$nextTick(() => {
        this.renderCount = this.renderCount + 1
      })
    },
    fetchFnTableList(limit, offset) {
      return assetDepreciationTableApi(this.moduleName, this.resourceId, {
        scheduleType: this.filterType,
        archived: this.resource.archived,
      }).then((data) => {
        this.showDownloadButton = (data || []).length > 0
        return data
      })
    },
    refresh() {
      this.getAssetDepreciationInfo()
      this.$nextTick(() => {
        this.renderCount = this.renderCount + 1
        // this.$refs.list.refresh()
      })
    },
    getAvailableAttachmentPassword(data) {
      const availableAttachmentPassword = data.protectedPassword
        ? getEncryptedPassword(data.protectedPassword)
        : getEncryptedPassword(this.attachmentPassword)
      return data.enabled ? availableAttachmentPassword : ''
    },
    handleDownloadAssetDepreciationDetails(data) {
      const requestData = {}
      requestData.model = this.moduleName
      requestData.exportFormat = data.exportFormat
      requestData.enabled = data.enabled
      requestData.attachmentPassword = this.getAvailableAttachmentPassword(data)
      const requestedColumns = []
      this.defaultSelectedColumns.forEach((c, i) =>
        requestedColumns.push({ propKey: c, orderNumber: i + 1 })
      )
      requestData.qualification = transformSearchCriteriasForServer(
        this.moduleName,
        [
          {
            operator: { value: 'in' },
            paramName: 'id',
            value: [this.resourceId],
            valueType: 'long',
          },
          ...(this.resource.archived
            ? [
                {
                  operator: { value: 'equal' },
                  paramName: 'removed',
                  value: true,
                  valueType: 'boolean',
                },
                {
                  operator: { value: 'equal' },
                  paramName: 'removed',
                  value: false,
                  valueType: 'boolean',
                },
              ]
            : []),
        ]
      )
      requestData.columns = requestedColumns
      requestData.type = 'AssetDepreciationDownloadDataRest'
      requestData.assetDepreciationScheduleType = this.filterType
      return downloadDepreciationApi(this.exportModuleName, requestData).catch(
        (e) => e
      )
    },
  },
}
</script>
